import Button from "@/Components/Button";
import Image from "next/image";
import React, { useState } from "react";
import BlogCards from "@/Components/DataDisplayCompoants/BlogCard";
import BlueCards from "@/Components/DataDisplayCompoants/BlueCard";
import GreenCards from "@/Components/DataDisplayCompoants/GreenCard";
import Breadcrumb from "@/Components/Breadcrumb/Breadcrumb";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

interface BannerProps {
  data?: any;
  breadcrumbs?: any;
  isMobile?: boolean;
}

const Banner: React.FC<BannerProps> = ({ data, isMobile, breadcrumbs }) => {
  const bannerAnimation = data?.banner_animation?.data.attributes.url || "";
  const bannerAnimationLink = data?.banner_animation_link || "";

  const [currentSlide, setCurrentSlide] = useState(0);

  const isVideoFile = (filename: string): boolean => {
    const ext = filename.toLowerCase().substring(filename.lastIndexOf("."));
    return [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"].includes(ext);
  };

  const isImageFile = (filename: string): boolean => {
    const ext = filename.toLowerCase().substring(filename.lastIndexOf("."));
    return [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp"].includes(
      ext,
    );
  };
  const [loaded, setLoaded] = useState(false);

  const componentsMap = {
    blog: BlogCards,
    blue: BlueCards,
    green: GreenCards,
  };

  type ComponentKey = keyof typeof componentsMap;

  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

  return (
    <>
      <section className="bg-background-dark relative  lg:pt-[86px] pt-[80px]">
        <span className="absolute bottom-0 xl:h-[192px] lg:h-[182px] mb-[-1px] h-[240px] w-full bg-white z-10 block"></span>
        <div className="container relative overflow-hidden">
          {/* Background media - conditionally render image or video based on file extension */}
          {bannerAnimationLink ? (
            // CDN video
            <div className="md:w-[60%] w-full object-contain h-[642px] max-h-full absolute top-0 sm:right-0 right-[-50%] z-0">
              <video
                src={bannerAnimationLink}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster="/images/poster/services-inner.webp"
                // @ts-expect-error - fetchPriority is valid but not in React types yet
                fetchPriority="high"
                className="w-full object-cover h-full max-h-full opacity-[23%]"
                aria-label="Banner background video (from link)"
              />
            </div>
          ) : bannerAnimation && isVideoFile(bannerAnimation) ? (
            <div className="md:w-[60%] w-full object-contain h-[642px] max-h-full absolute top-0 sm:right-0 right-[-50%]  z-0">
              <video
                src={`${baseUrl}${bannerAnimation}`}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster="/images/poster/services-inner.webp"
                // @ts-expect-error - fetchPriority is valid but not in React types yet
                fetchPriority="high"
                className="w-full object-cover h-full max-h-full opacity-[23%] "
                aria-label="Banner background video"
              />
            </div>
          ) : bannerAnimation && isImageFile(bannerAnimation) ? (
            <div className="md:w-[60%] w-full object-contain h-[642px] max-h-full absolute top-0 sm:right-0 right-[-50%] z-0">
              <Image
                src={`${baseUrl}${bannerAnimation}`}
                alt="Banner background image"
                width={500}
                height={642}
                unoptimized
                className="w-full object-cover h-full max-h-full opacity-[23%]"
                loading="eager"
                priority
              />
            </div>
          ) : null}
          <div className="relative z-20">
            <div className="xl:pt-[40px] md:pt-[31px] pt-[13px] xl:pb-[64px] md:pb-[64px] pb-[48px] ">
              {breadcrumbs?.item?.length > 0 && (
                <Breadcrumb
                  items={breadcrumbs?.item ?? []}
                  className="mb-[16px] lg:mb-[32px] xl:mb-[40px]"
                />
              )}
              <h1 className="text-display font-extrabold text-primary-text-white uppercase mt-4 max-w-[1230px]">
                {data?.title}
              </h1>
              <div className="flex justify-start items-stretch gap-6 mt-4">
                <div className="w-1.5 bg-[linear-gradient(179.61deg,_#8ABC00_3.58%,_#0086E6_97.78%)]"></div>
                <div>
                  <p className="text-secondary-text-white text-subtitle-2 font-regular max-w-[578px]">
                    {data?.description}
                  </p>

                  {data?.Cta?.label && data?.Cta?.href && (
                    <a href={data?.Cta?.href} className="inline-block">
                      <Button className="mt-4" label={data?.Cta?.label} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {!isMobile && (
          <div className="hidden md:block">
            <div className="container relative z-30">
              <div className="xl:grid-cols-3 md:grid-cols-12 gap-[30px] md:grid hidden">
                <>
                  {data?.bannerCard?.map((card: any, index: number) => {
                    const Component =
                      componentsMap[card.varient as ComponentKey];
                    if (!Component) return null;

                    if (card.varient === "blog" && index === 0) {
                      return (
                        <div key={index} className="xl:col-span-2 col-span-6">
                          <Component
                            imageUrl={
                              card.image
                                ? `${baseUrl}${card.image.data.attributes.url}`
                                : "/images/blog-card.jpg"
                            }
                            label={card.title}
                            title={card.title}
                            link={card.link.href}
                            details={card.description}
                            classname="h-full ipad-services"
                          />
                        </div>
                      );
                    }

                    if (card.varient === "blue") {
                      return (
                        <div
                          key={index}
                          className="relative xl:col-span-1  col-span-6"
                        >
                          <Component
                            imageUrl="/images/Blue_Vector.png"
                            label={card.title}
                            link={card.link.href}
                            title={card.title}
                            details={card.description}
                            classname="bg-new-image ipad-services h-full"
                          />
                        </div>
                      );
                    }

                    if (card.varient === "green") {
                      return (
                        <div
                          key={index}
                          className="relative xl:col-span-1  col-span-6"
                        >
                          <Component
                            imageUrl={"/images/Green_Vector.png"}
                            label={card.title}
                            link={card.link.href}
                            title={card.title}
                            classname="bg-new-image ipad-services h-full"
                            details={card.description}
                          />
                        </div>
                      );
                    }

                    if (card.varient === "blog" && index === 3) {
                      return (
                        <div key={index} className="xl:col-span-2 col-span-6">
                          <Component
                            imageUrl={
                              card.image
                                ? `${baseUrl}${card.image.data.attributes.url}`
                                : "/images/blog-card.jpg"
                            }
                            label={card.title}
                            title={card.title}
                            link={card.link.href}
                            classname="h-full ipad-services"
                            details={card.description}
                          />
                        </div>
                      );
                    }

                    return null;
                  })}
                </>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Slider */}
        {isMobile && (
          <div className="block md:hidden">
            <div className="relative z-30">
              {/* Mobile Swiper Slider */}
              <div className="md:hidden block mx-[20px]">
                <Swiper
                  modules={[Autoplay]}
                  spaceBetween={15}
                  slidesPerView={1}
                  centeredSlides={true}
                  loop={true}
                  autoplay={{ delay: 3000, disableOnInteraction: false }}
                  // style={{ minWidth: "356px" }}
                  onSlideChange={(swiper) => setCurrentSlide(swiper.realIndex)}
                  onSwiper={() => setLoaded(true)}
                >
                  {data?.bannerCard?.map((card: any, index: number) => {
                    const Component =
                      componentsMap[card.varient as ComponentKey];
                    if (!Component) return null;

                    return (
                      <SwiperSlide key={index}>
                        <div>
                          <Component
                            imageUrl={
                              card.image
                                ? `${baseUrl}${card?.image?.data?.attributes?.url}`
                                : card.varient === "blog"
                                  ? "/images/blog-card.jpg"
                                  : card.varient === "blue"
                                    ? "/images/Blue_Vector.png"
                                    : "/images/Green_Vector.png"
                            }
                            title={card.title}
                            details={card.description}
                            label={card.title}
                            link={card.link.href}
                            classname={
                              card.varient === "green" ? "bg-new-image" : ""
                            }
                          />
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              </div>

              {/* Custom Slider Dots for Mobile */}
              <div className="md:hidden block bg-white min-w-[224px] max-w-[224px] mx-auto mt-[24px] ">
                {loaded && data?.bannerCard?.length > 0 && (
                  <div className="dots container p-0 flex justify-center gap-2">
                    {data.bannerCard.map((_: any, idx: number) => (
                      <button
                        aria-label="dot"
                        key={idx}
                        onClick={() => {
                          const swiperEl = document.querySelector(
                            ".swiper",
                          ) as any;
                          if (swiperEl && swiperEl.swiper) {
                            swiperEl.swiper.slideToLoop(idx);
                          }
                        }}
                        className={`dot${currentSlide === idx ? " active" : ""}`}
                      >
                        <span
                          className={currentSlide === idx ? "active" : ""}
                        ></span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* <Image
          src="/images/ServicesPage/top-corner.png"
          alt=""
          width={500}
          height={200}
          className="w-[40%] object-cover h-[100%] sm:h-[761px] max-h-full absolute top-0 left-0 z-0 "
          loading="eager"
          unoptimized
        />
        <Image
          src="/images/ServicesPage/bottom-corner.png"
          alt=""
          width={500}
          height={200}
          className="w-[50%] h-[100%] max-h-full absolute bottom-0 right-0 z-0"
          loading="eager"
          unoptimized
        /> */}
      </section>
    </>
  );
};

export default Banner;
